import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';
import {
  RETRY_CONFIG,
  getLoggedInUserInfoRetryDelay,
  isRetryableLoggedInUserInfoError,
} from 'pages/Dashboard/hooks/userInfoRetry';
import {
  getApiUserGetLoggedInEMRUserInfo,
} from 'endpoints';
import {
  useImpersonation,
} from 'core/context/impersonation';
import isNil from 'lodash/isNil';
import {
  AxiosError,
  isAxiosError,
} from 'axios';
import {
  LoggedInUserInfoAccess,
} from 'views/EMR/access';

export default function useUserInfo(enabled: boolean = true): LoggedInUserInfoAccess | null {
  const { impersonation, isImpersonating } = useImpersonation();

  const { data, isError, error } = usePrefetchedData<LoggedInUserInfoAccess>({
    key: ['logged-user-info', impersonation?.token ?? ''],
    fetchFn: getApiUserGetLoggedInEMRUserInfo,
    enabled,
    retry: (failureCount, retryError) => (
      failureCount < RETRY_CONFIG.maxRetries
      && isRetryableLoggedInUserInfoError(retryError)
    ),
    retryDelay: getLoggedInUserInfoRetryDelay,
  });

  if (isError && isNil(data)) {
    const axiosError = isAxiosError(error) ? error as AxiosError : null;
    console.error('Retrieving logged-in user information failed:', error, {
      isImpersonating,
      status: axiosError?.response?.status,
      statusText: axiosError?.response?.statusText,
      responseData: axiosError?.response?.data,
      message: axiosError?.message ?? error?.message ?? error,
      code: axiosError?.code,
      url: axiosError?.config?.url,
    });

    const status = axiosError?.response?.status;
    throw Error(
      `Failed to retrieve logged-in user information${!isNil(status) ? ` (HTTP ${status})` : ''}.`,
    );
  }

  return data ?? null;
}
